import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import AgenciasService, {
  Agencia,
  AgenciaCreate,
  AgenciaUpdate,
  AgenciaFilter,
} from '../../services/agenciasService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const AgenciasPage: React.FC = () => {
  const theme = useTheme();
  
  // Estado de listagem
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [agenciasPrincipais, setAgenciasPrincipais] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<AgenciaFilter>({});
  
  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentAgencia, setCurrentAgencia] = useState<Agencia | null>(null);
  const [newAgencia, setNewAgencia] = useState<AgenciaCreate>({ nome: '' });
  const [editAgencia, setEditAgencia] = useState<AgenciaUpdate>({});

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Adicionar ref para debounce
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      
      // Buscar agências para uso como "agência principal"
      const agenciasData = await AgenciasService.getAllAgencias();
      setAgenciasPrincipais(agenciasData);
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchAgencias = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      const data = await AgenciasService.getAgencias(filter, skip, limit);
      setAgencias(data);
      
      // Obter contagem total para paginação
      const count = await AgenciasService.getAgenciasCount(filter);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar agências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar agências',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar agências ao iniciar a página ou mudar filtros/paginação
    fetchAgencias();
    // Carregar referências
    carregarReferencias();
  }, [fetchAgencias, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof AgenciaFilter, value: string | number | undefined) => {
    // Limpar timeout anterior se existir
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Atualizar o filtro imediatamente na UI
    setFilter((prev) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));

    // Debounce: aguardar 500ms antes de aplicar a busca
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Resetar paginação ao mudar filtro
    }, 500);
  };

  // Função para limpar filtro de busca
  const clearSearchFilter = () => {
    setFilter((prev) => ({
      ...prev,
      nome: undefined
    }));
    setPage(0);
  };

  const handleCreateAgencia = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newAgencia.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da agência é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await AgenciasService.createAgencia(newAgencia);
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Agência criada com sucesso',
        severity: 'success',
      });
      
      // Limpar formulário
      setNewAgencia({ nome: '' });
      
      // Recarregar dados
      fetchAgencias();
      
      // Atualizar agências principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao criar agência:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar agência',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (agencia: Agencia) => {
    setCurrentAgencia(agencia);
    // Inicializar dados para edição
    setEditAgencia({
      nome: agencia.nome,
      principal_id: agencia.principal_id
    });
    
    setOpenEditDialog(true);
  };

  const handleEditAgencia = async () => {
    if (!currentAgencia) return;

    try {
      // Verificar campos obrigatórios
      if (!editAgencia.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da agência é obrigatório',
          severity: 'error',
        });
        return;
      }

      await AgenciasService.updateAgencia(currentAgencia.id, editAgencia);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Agência atualizada com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentAgencia(null);
      setEditAgencia({});
      
      // Recarregar dados
      fetchAgencias();
      
      // Atualizar agências principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao atualizar agência:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar agência',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (agencia: Agencia) => {
    setCurrentAgencia(agencia);
    setOpenDeleteDialog(true);
  };

  const handleDeleteAgencia = async () => {
    if (!currentAgencia) return;

    try {
      await AgenciasService.deleteAgencia(currentAgencia.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Agência excluída com sucesso',
        severity: 'success',
      });
      
      // Limpar estados
      setCurrentAgencia(null);
      
      // Recarregar dados
      fetchAgencias();
      
      // Atualizar agências principais para seleção
      carregarReferencias();
    } catch (error) {
      console.error('Erro ao excluir agência:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao excluir agência',
        severity: 'error',
      });
    }
  };

  // Handler para alterações em selects
  const handleSelectChange = (event: SelectChangeEvent<number | string | undefined>, setter: Function) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setter((prev: any) => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <SidebarLayout>
      <Box p={3}>
        <Typography variant="h4" gutterBottom display="flex" alignItems="center">
          <BusinessIcon sx={{ mr: 2 }} />
          Gerenciamento de Agências
        </Typography>
        
        {/* Área de filtros */}
        <Paper 
          elevation={2} 
          sx={{ p: 2, mb: 3, backgroundColor: theme.palette.background.default }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Buscar por nome"
                variant="outlined"
                size="small"
                value={filter.nome || ''}
                onChange={(e) => handleFilterChange('nome', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filter.nome ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearchFilter}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenCreateDialog(true)}
                sx={{ height: '100%' }}
              >
                Nova Agência
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela de Agências */}
        <Paper elevation={3} sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Agência Principal</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : agencias.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nenhuma agência encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  agencias.map((agencia) => (
                    <TableRow key={agencia.id} hover>
                      <TableCell>{agencia.id}</TableCell>
                      <TableCell>{agencia.nome}</TableCell>
                      <TableCell>
                        {agenciasPrincipais.find(a => a.id === agencia.principal_id)?.nome || '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(agencia)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(agencia)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {/* Modal de Criação */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Agência</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newAgencia.nome || ''}
                onChange={(e) => setNewAgencia({ ...newAgencia, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-agencia-principal-label">Agência Principal</InputLabel>
                <Select
                  labelId="new-agencia-principal-label"
                  label="Agência Principal"
                  name="principal_id"
                  value={newAgencia.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setNewAgencia)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {agenciasPrincipais.map((agencia) => (
                    <MenuItem key={agencia.id} value={agencia.id}>
                      {agencia.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateAgencia}
            disabled={!newAgencia.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Editar Agência</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={editAgencia.nome || ''}
                onChange={(e) => setEditAgencia({ ...editAgencia, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-agencia-principal-label">Agência Principal</InputLabel>
                <Select
                  labelId="edit-agencia-principal-label"
                  label="Agência Principal"
                  name="principal_id"
                  value={editAgencia.principal_id || ''}
                  onChange={(e) => handleSelectChange(e, setEditAgencia)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {agenciasPrincipais
                    .filter(agencia => currentAgencia ? agencia.id !== currentAgencia.id : true)
                    .map((agencia) => (
                      <MenuItem key={agencia.id} value={agencia.id}>
                        {agencia.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleEditAgencia}
            disabled={!editAgencia.nome}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a agência "{currentAgencia?.nome}"?
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteAgencia}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default AgenciasPage;
